import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, MessageCircle, Send } from "lucide-react";
import { waLink } from "@/data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-3 text-cream placeholder:text-cream/35 font-light transition-colors duration-300";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.3em] uppercase text-gold">{label}{required && " *"}</span>
    {children}
  </label>
);

export const QuoteSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", event_type: "Wedding", event_date: "", event_location: "", guests: "", cuisine: "", budget: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waMsg = `Hi Momay Caterers, I'm ${form.name}. I'm planning a ${form.event_type}${form.event_date ? ` on ${form.event_date}` : ""}${form.event_location ? ` at ${form.event_location}` : ""}${form.guests ? ` for about ${form.guests} guests` : ""}. I'd like a custom quote.`;

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: "quote_form" });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong sending your enquiry. Please try WhatsApp instead — we reply fast.");
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-espresso grain relative py-24 md:py-36">
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <SectionHeading chapter="09" eyebrow="Get a Quote" title="Planning Your Next" script="celebration?" dark />
          <Reveal delay={0.15}>
            <p className="mt-8 text-cream/60 font-light leading-relaxed">
              Tell us what you're planning and let Momay create the perfect catering experience.
              Share a few details and our team will get back to you with a customised menu and quote.
            </p>
            <div className="mt-10 space-y-3 text-cream/70 text-sm font-light">
              <p data-testid="quote-context-wedding">Prefer to chat first? We have dedicated WhatsApp lines for quick questions:</p>
              <div className="flex flex-col gap-2">
                <a data-testid="wa-wedding-link" className="text-gold hover:text-cream transition-colors underline underline-offset-4 decoration-gold/40" target="_blank" rel="noopener noreferrer" href={waLink("Hi Momay Caterers, I'd like to know more about wedding catering menus and packages.")}>WhatsApp about Wedding Catering</a>
                <a data-testid="wa-corporate-link" className="text-gold hover:text-cream transition-colors underline underline-offset-4 decoration-gold/40" target="_blank" rel="noopener noreferrer" href={waLink("Hi Momay Caterers, I'd like to know more about corporate event catering.")}>WhatsApp about Corporate Catering</a>
                <a data-testid="wa-jain-link" className="text-gold hover:text-cream transition-colors underline underline-offset-4 decoration-gold/40" target="_blank" rel="noopener noreferrer" href={waLink("Hi Momay Caterers, I'd like to know more about your Jain catering options.")}>WhatsApp about Jain Catering</a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {status === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-full min-h-[420px] flex flex-col items-center justify-center text-center border gold-hairline p-10"
                data-testid="quote-success"
              >
                <CheckCircle2 size={52} className="text-gold" strokeWidth={1.2} />
                <h3 className="mt-6 font-serif text-3xl text-cream">Thank you, {form.name.split(" ")[0]}.</h3>
                <p className="mt-3 text-cream/60 font-light max-w-md">
                  Your enquiry has been received. Our team will reach out shortly — or continue right now on WhatsApp for the fastest response.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                  <a data-testid="quote-success-whatsapp" href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gold text-espresso px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-medium hover:bg-cream transition-colors">
                    <MessageCircle size={15} /> Continue on WhatsApp
                  </a>
                  <button data-testid="quote-send-another" onClick={() => { setStatus("idle"); setForm({ ...form, message: "" }); }} className="border border-cream/30 text-cream px-7 py-3.5 text-xs tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors">
                    Send Another
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={submit}
                data-testid="quote-form"
                className="grid sm:grid-cols-2 gap-x-8 gap-y-7 border gold-hairline p-7 md:p-10"
              >
                <Field label="Name" required><input data-testid="quote-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" /></Field>
                <Field label="Phone Number" required><input data-testid="quote-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Your contact number" /></Field>
                <Field label="Event Type" required>
                  <select data-testid="quote-event-type" value={form.event_type} onChange={set("event_type")} className={`${inputCls} bg-espresso`}>
                    {["Wedding", "Engagement", "Birthday / Party", "Corporate Event", "Jain Function", "Social Gathering", "Private Event", "Other"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Event Date"><input data-testid="quote-date" type="date" value={form.event_date} onChange={set("event_date")} className={`${inputCls} [color-scheme:dark]`} /></Field>
                <Field label="Event Location"><input data-testid="quote-location" value={form.event_location} onChange={set("event_location")} className={inputCls} placeholder="City / venue" /></Field>
                <Field label="Number of Guests">
                  <select data-testid="quote-guests" value={form.guests} onChange={set("guests")} className={`${inputCls} bg-espresso`}>
                    <option value="">Select</option>
                    {["50–100", "100–250", "250–500", "500–1000", "1000+"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Preferred Cuisine">
                  <select data-testid="quote-cuisine" value={form.cuisine} onChange={set("cuisine")} className={`${inputCls} bg-espresso`}>
                    <option value="">Select</option>
                    {["Gujarati", "Punjabi / North Indian", "South Indian", "Multi-Cuisine", "Jain", "Not Sure Yet"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Approximate Budget">
                  <select data-testid="quote-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} bg-espresso`}>
                    <option value="">Select</option>
                    {["Under ₹50,000", "₹50,000 – ₹1,00,000", "₹1,00,000 – ₹3,00,000", "₹3,00,000+", "Prefer to Discuss"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message"><textarea data-testid="quote-message" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Anything we should know — themes, counters, timings…" /></Field>
                </div>
                {error && <p data-testid="quote-error" className="sm:col-span-2 text-red-300 text-sm">{error}</p>}
                <div className="sm:col-span-2">
                  <button
                    data-testid="quote-submit-btn"
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full group inline-flex items-center justify-center gap-3 bg-gold text-espresso px-8 py-5 text-xs md:text-sm tracking-[0.3em] uppercase font-medium hover:bg-cream transition-colors duration-300 disabled:opacity-60"
                  >
                    {status === "sending" ? "Sending…" : "Get My Custom Quote"}
                    <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
};
